import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, CheckCircle, XCircle, Calendar, DollarSign, User, Building, FileText, Clock } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import { useFinancasStore } from '@/stores/financas.store'
import { mockMinisterios } from '@/mocks'
import type { SolicitacaoVerba } from '@/mocks/financas.mock'

export function SolicitacaoDetalhePage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [solicitacao, setSolicitacao] = useState<SolicitacaoVerba | null>(null)
  const [loading, setLoading] = useState(true)
  const { solicitacoes } = useFinancasStore()

  useEffect(() => {
    // Buscar a solicitação no store
    setTimeout(() => {
      const solicitacaoEncontrada = solicitacoes.find(s => s.id === id)
      setSolicitacao(solicitacaoEncontrada || null)
      setLoading(false)
    }, 500)
  }, [id, solicitacoes])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pendente':
        return 'bg-yellow-100 text-yellow-800'
      case 'aprovado':
        return 'bg-green-100 text-green-800'
      case 'rejeitado':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pendente':
        return 'Pendente'
      case 'aprovado':
        return 'Aprovado'
      case 'rejeitado':
        return 'Rejeitado'
      default:
        return status
    }
  }

  const getPrioridadeColor = (prioridade: string) => {
    switch (prioridade) {
      case 'baixa':
        return 'bg-blue-100 text-blue-800'
      case 'media':
        return 'bg-yellow-100 text-yellow-800'
      case 'alta':
        return 'bg-orange-100 text-orange-800'
      case 'urgente':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getPrioridadeText = (prioridade: string) => {
    switch (prioridade) {
      case 'baixa':
        return 'Baixa'
      case 'media':
        return 'Média'
      case 'alta':
        return 'Alta'
      case 'urgente':
        return 'Urgente'
      default:
        return prioridade
    }
  }

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando detalhes da solicitação...</p>
          </div>
        </div>
      </div>
    )
  }

  if (!solicitacao) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Solicitação não encontrada</h2>
            <p className="text-muted-foreground mb-4">
              A solicitação com ID "{id}" não foi encontrada.
            </p>
            <Button onClick={() => navigate('/financas/solicitacoes-ministeriais')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar para Solicitações
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Buscar o nome do ministério
  const ministerio = mockMinisterios.find(m => m.id === solicitacao.ministerioId)
  const nomeMinisterio = ministerio?.nome || 'Ministério não encontrado'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/financas/solicitacoes-ministeriais')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{solicitacao.titulo}</h1>
            <p className="text-muted-foreground">Detalhes da Solicitação #{solicitacao.id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={getStatusColor(solicitacao.status)}>
            {getStatusText(solicitacao.status)}
          </Badge>
          <Badge className={getPrioridadeColor(solicitacao.prioridade || 'media')}>
            {getPrioridadeText(solicitacao.prioridade || 'media')}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informações Principais */}
        <div className="lg:col-span-2 space-y-6">
          {/* Informações Gerais */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Informações Gerais
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Ministério</label>
                  <p className="flex items-center gap-2">
                    <Building className="h-4 w-4" />
                    {nomeMinisterio}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Solicitante</label>
                  <p className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    {solicitacao.solicitante}
                  </p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Motivo</label>
                <p>{solicitacao.motivo}</p>
              </div>
            </CardContent>
          </Card>

          {/* Informações Financeiras */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Informações Financeiras
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Valor Solicitado</label>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(solicitacao.valor)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Data da Solicitação</label>
                <p className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {new Date(solicitacao.data).toLocaleDateString('pt-BR')}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Histórico */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Histórico
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <div className="flex-1">
                    <p className="font-medium">Solicitação criada</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(solicitacao.data).toLocaleDateString('pt-BR')} às {new Date(solicitacao.data).toLocaleTimeString('pt-BR')}
                    </p>
                  </div>
                </div>
                
                {solicitacao.status === 'aprovado' && (
                  <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <div className="flex-1">
                      <p className="font-medium">Solicitação aprovada</p>
                      <p className="text-sm text-muted-foreground">Aprovada pela administração</p>
                    </div>
                  </div>
                )}
                
                {solicitacao.status === 'rejeitado' && (
                  <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
                    <XCircle className="w-5 h-5 text-red-500" />
                    <div className="flex-1">
                      <p className="font-medium">Solicitação rejeitada</p>
                      <p className="text-sm text-muted-foreground">Rejeitada pela administração</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default SolicitacaoDetalhePage